import { ReactNode } from 'react';

import { selectCurrentCourseId } from '../../db/courses/selectors';
import { selectCourseUserProfile } from '../../db/user-profiles/selectors';
import { useAppSelector } from '../../store/hooks';
import { CourseRole } from '../../types';

interface SubtitleWithEmailId {
  isEmailIdNeeded: true;
  isCourseRoleNeeded: false;
  subtitle?: ReactNode | ((name?: string, emailId?: string) => ReactNode);
}

interface SubtitleWithCourseRole {
  isEmailIdNeeded: false;
  isCourseRoleNeeded: true;
  subtitle?: ReactNode | ((name?: string, courseRole?: CourseRole, isRemovedMember?: boolean) => ReactNode);
}

interface SubtitleWithEmailIdAndCourseRole {
  isEmailIdNeeded: true;
  isCourseRoleNeeded: true;
  subtitle?:
    | ReactNode
    | ((name?: string, emailId?: string, courseRole?: CourseRole, isRemovedMember?: boolean) => ReactNode);
}

interface SubtitleWithoutEmailIdAndCourseRole {
  isEmailIdNeeded: false;
  isCourseRoleNeeded: false;
  subtitle?: ReactNode | ((name?: string) => ReactNode);
}

export type UseCellSubtitle = {
  avatarId: MongoId;
} & (
  | SubtitleWithEmailId
  | SubtitleWithCourseRole
  | SubtitleWithEmailIdAndCourseRole
  | SubtitleWithoutEmailIdAndCourseRole
);

const useCellSubtitle = ({ avatarId, ...props }: UseCellSubtitle) => {
  const currentCourseId = useAppSelector((state) => selectCurrentCourseId(state));

  const courseUserProfile = useAppSelector((state) => selectCourseUserProfile(state, avatarId));
  const { name, emailId, courseUserDetails } = courseUserProfile || {};
  const courseRole = currentCourseId ? courseUserDetails?.[currentCourseId]?.role : undefined;
  const isRemovedMember = currentCourseId ? courseUserDetails?.[currentCourseId]?.isRemovedMember : undefined;

  if (!props.subtitle) {
    return {
      subtitle: null,
      isRemovedMember: undefined,
    };
  }

  if (typeof props.subtitle !== 'function') {
    return {
      subtitle: props.subtitle,
      isRemovedMember,
    };
  }

  if (props.isEmailIdNeeded && props.isCourseRoleNeeded) {
    return {
      subtitle: props.subtitle(name, emailId, courseRole, isRemovedMember),
      isRemovedMember,
    };
  }

  if (props.isEmailIdNeeded) {
    return {
      subtitle: props.subtitle(name, emailId),
      isRemovedMember,
    };
  }

  if (props.isCourseRoleNeeded) {
    return {
      subtitle: props.subtitle(name, courseRole, isRemovedMember),
      isRemovedMember,
    };
  }

  return {
    subtitle: props.subtitle(name),
    isRemovedMember,
  };
};

export default useCellSubtitle;
